import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import { ClientThemeProvider } from './contexts/ClientThemeContext';
import { ClientAuthProvider, useClientAuth } from './contexts/ClientAuthContext';
import { PWAProvider } from './contexts/PWAContext';
import ClientLayout from './layouts/ClientLayout';
import ClientLogin from './pages/ClientLogin';
import ClientDashboard from './pages/ClientDashboard';
import ClientInvoices from './pages/ClientInvoices';
import ClientInvoiceDetail from './pages/ClientInvoiceDetail';
import ClientPayments from './pages/ClientPayments';
import ClientSupport from './pages/ClientSupport';
import KnowledgeArticleDetail from './pages/KnowledgeArticleDetail';
import ClientProfile from './pages/ClientProfile';
import ClientTraffic from './pages/ClientTraffic';
import ClientNotifications from './pages/ClientNotifications';
import NotFoundPage from './pages/NotFoundPage';
import ClientResetPassword from './pages/ClientResetPassword';
import ClientPromotions from './pages/ClientPromotions';

// Usar el mismo proveedor de contexto simple definido en App.js
// Esto evita la dependencia directa de @tanstack/react-query
const QueryContext = React.createContext(null);

// Cliente de consulta simplificado para el portal del cliente
const createClientQueryClient = () => {
    return {
        queries: new Map(),
        mutations: new Map(),
        defaultOptions: {
            queries: {
                refetchOnWindowFocus: false,
                retry: 1,
                staleTime: 5 * 1000,
                cacheTime: 10 * 60 * 1000,
            },
        },

        // Métodos básicos para reemplazar la funcionalidad de React Query
        fetchQuery: async (key, queryFn) => {
            try {
                const result = await queryFn();
                return result;
            } catch (error) {
                throw error;
            }
        },

        invalidateQueries: (key) => {
            // Lógica simple para invalidar consultas
        }
    };
};

const clientQueryClient = createClientQueryClient();

export const ClientQueryProvider = ({ children }) => {
    return (
        <QueryContext.Provider value={clientQueryClient}>
            {children}
        </QueryContext.Provider>
    );
};

export const useClientQueryClient = () => {
    return React.useContext(QueryContext);
};

// Componente para proteger rutas
const ProtectedRoute = ({ children }) => {
    const { clientToken } = useClientAuth();
    const location = useLocation();

    if (!clientToken) {
        return <Navigate to="/client/login" state={{ from: location }} replace />;
    }

    return children;
};

const ClientPortalApp = () => {
    console.log('App - Renderizando ClientPortalApp');

    return (
        <PWAProvider>
            <ClientQueryProvider>
                <ClientThemeProvider>
                    <CssBaseline />
                    <ClientAuthProvider>
                        <Routes>
                            <Route path="/login" element={<ClientLogin />} />
                            <Route path="/reset-password" element={<ClientResetPassword />} />

                            <Route path="/" element={
                                <ProtectedRoute>
                                    <ClientLayout />
                                </ProtectedRoute>
                            }>
                                <Route index element={<Navigate to="dashboard" replace />} />
                                <Route path="dashboard" element={<ClientDashboard />} />
                                <Route path="invoices" element={<ClientInvoices />} />
                                <Route path="invoices/:id" element={<ClientInvoiceDetail />} />
                                <Route path="payments" element={<ClientPayments />} />
                                <Route path="support" element={<ClientSupport />} />
                                <Route path="support/knowledge/:id" element={<KnowledgeArticleDetail />} />
                                <Route path="profile" element={<ClientProfile />} />
                                <Route path="traffic" element={<ClientTraffic />} />
                                <Route path="notifications" element={<ClientNotifications />} />
                                <Route path="promotions" element={<ClientPromotions />} />
                                <Route path="*" element={<NotFoundPage />} />
                            </Route>
                        </Routes>
                    </ClientAuthProvider>
                </ClientThemeProvider>
            </ClientQueryProvider>
        </PWAProvider>
    );
};

export default ClientPortalApp;